import { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Cookies from "js-cookie";

import { APIGetPartnerProfile } from "./api_partner";
import { APIPartnerLogout } from "./api_partner_auth";


export const PartnerContext = createContext(null);
export const usePartner = () => useContext(PartnerContext);


// Названия кук/ключей вынесены — чтобы случайно не пересеклись с user-токеном.
export const PARTNER_TOKEN_COOKIE = 'partnerToken';


export default function PartnerProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [partnerToken, setPartnerToken] = useState(null);
    const [partner, setPartner] = useState(null);
    const [bankDetails, setBankDetails] = useState(null);

    // 'idle' | 'verifying' | 'authed' | 'unauthed'
    const [authStatus, setAuthStatus] = useState('idle');

    // Загрузка профиля партнёра по токену.
    const loadPartner = async (token) => {
        try {
            const res = await APIGetPartnerProfile(token);
            setPartner(res?.partner || null);
            setBankDetails(res?.bank_details || null);
            return true;
        } catch (e) {
            console.error('Ошибка загрузки профиля партнёра:', e?.response?.status);
            // 401/403 → токен невалиден
            if (e?.response?.status === 401 || e?.response?.status === 403) {
                return false;
            }
            // Сеть/500 — оставляем токен, но партнёра нет.
            return true;
        }
    };

    // Применить успешный логин: положить токен в куку и состояние, загрузить профиль.
    const applyLoginSuccess = async ({ token, partner: partnerDto }) => {
        Cookies.set(PARTNER_TOKEN_COOKIE, token, { expires: 30 });
        setPartnerToken(token);
        if (partnerDto) setPartner(partnerDto);
        else await loadPartner(token);
        setAuthStatus('authed');
    };

    const logout = async () => {
        try {
            if (partnerToken) await APIPartnerLogout(partnerToken);
        } catch (e) { /* не важно */ }
        Cookies.remove(PARTNER_TOKEN_COOKIE);
        setPartnerToken(null);
        setPartner(null);
        setBankDetails(null);
        setAuthStatus('unauthed');
        navigate('/partner/login');
    };

    // Проверка токена при загрузке + при смене маршрута (на случай открытия закладки).
    useEffect(() => {
        const verify = async () => {
            const token = Cookies.get(PARTNER_TOKEN_COOKIE);
            if (!token) {
                setAuthStatus('unauthed');
                return;
            }
            setAuthStatus('verifying');
            const ok = await loadPartner(token);
            if (ok) {
                setPartnerToken(token);
                setAuthStatus('authed');
            } else {
                Cookies.remove(PARTNER_TOKEN_COOKIE);
                setAuthStatus('unauthed');
            }
        };
        verify();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Если зашли на защищённый /partner/* без токена — на /partner/login.
    useEffect(() => {
        const isPartnerArea = location.pathname.startsWith('/partner');
        const isPublic =
            location.pathname === '/partner/login' ||
            location.pathname === '/partner/forgot-password' ||
            location.pathname === '/partner/reset-password';
        if (isPartnerArea && !isPublic && authStatus === 'unauthed') {
            navigate('/partner/login', { replace: true });
        }
    }, [location.pathname, authStatus, navigate]);

    const value = {
        partnerToken, setPartnerToken,
        partner, setPartner,
        bankDetails, setBankDetails,
        authStatus,
        applyLoginSuccess,
        logout,
        reloadPartner: () => partnerToken && loadPartner(partnerToken),
    };

    return (
        <PartnerContext.Provider value={value}>
            {children}
        </PartnerContext.Provider>
    );
}